import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  Button,
  Row,
  Col,
  Avatar,
  Table,
  Space,
  Typography,
  notification,
} from "antd";
import { RepresentationModal } from "@/features/representation-modal/index.ts";
import { BasketIcon } from "@/shared/assets/svg/icons/index.ts";
import {
  useAddProjectAdmin,
  useRemoveProjectAdmin,
  projectQueries,
} from "@entities/project";
import { Can } from "@shared/lib/casl";
import { useCoordinator, useDisclosure } from "@shared/lib/hooks";
import { NOTIFICATION_MESSAGES } from "@shared/utils";
import "./project-detail.scss";
import { ProjectAvatar } from "../index.ts";

export default function ProjectDetail() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [key, setKey] = useState<number | null>(1);
  const { projectId } = useCoordinator() as { projectId: string };

  const { data: admins, isLoading } = useQuery(
    projectQueries.getProjectAdmin({ projectId }),
  );

  const { data: project } = useQuery(projectQueries.getProject({ projectId }));

  const {
    mutateAsync: addAdmin,
    isPending,
    error,
  } = useAddProjectAdmin(projectId || "");
  const { mutateAsync: mutateAsyncRemove, isPending: isPendingRemove } =
    useRemoveProjectAdmin(projectId || "");
  const handleAddAdmin = (id?: string) => {
    if (id) {
      addAdmin(id)
        .then(() => {
          onClose();
          notification.success({
            message: NOTIFICATION_MESSAGES.SUCCESS_ALT, // "Успех"
            description: NOTIFICATION_MESSAGES.ADMIN_ADDED_DESC, // "Удалось добавить админа"
            placement: "topRight",
          });
          setKey((prev) => (prev ? prev + 1 : 1));
        })

        .catch(() => {
          notification.error({
            message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
            description: NOTIFICATION_MESSAGES.ADMIN_ADD_FAILED_DESC, // "Не удалось добавить админа"
            placement: "topRight",
          });
        });
    }
  };

  const handleRemoveAdmin = (id?: string) => {
    if (id) {
      mutateAsyncRemove(id)
        .then(() => {
          onClose();
          notification.success({
            message: NOTIFICATION_MESSAGES.SUCCESS_ALT, // "Успех"
            description: NOTIFICATION_MESSAGES.ADMIN_REMOVED_DESC, // "Удалось удалить админа"
            placement: "topRight",
          });
        })
        .catch(() => {
          notification.error({
            message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
            description: NOTIFICATION_MESSAGES.ADMIN_REMOVE_FAILED_DESC, // "Не удалось удалить админа"
            placement: "topRight",
          });
        });
    }
  };

  const onCopyIdClick = () => {
    navigator.clipboard.writeText(project?.projectId ?? "").then(() => {
      notification.success({
        message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
        description: NOTIFICATION_MESSAGES.ID_COPIED_DESC, // "Id скопирован в буфер обмена"
        placement: "topRight",
        showProgress: false,
      });
    });
  };

  const transformedData =
    admins?.userInfo?.map((admin) => ({
      key: admin.userId,
      // avatar: admin.userAvatarId
      //   ? `https://your-image-service/${admin.userAvatarId}`
      //   : `https://xsgames.co/randomusers/avatar.php?g=male`,
      avatar: `${admin?.userFirstName?.[0]}${admin?.userMiddleName?.[0]}`,
      fullName: `${admin.userSurname} ${admin.userFirstName} ${admin.userMiddleName ?? ""}`,
      organization: admin.userCompanies?.[0]?.shortName || "Не указано",
      position: admin.userCompanies?.[0]?.position || "Не указано",
      email: admin.userEmail,
      phone: admin.userMobilePhone,
    })) || [];

  const columns = [
    {
      title: "ФИО",
      dataIndex: "fullName",
      key: "fullName",
      render: (text: string, record: any) => (
        <Space>
          <Avatar style={{ width: "30px", height: "30px" }}>
            {record.avatar}
          </Avatar>
          <Typography.Text>{text}</Typography.Text>
        </Space>
      ),
    },
    {
      title: "Организация",
      dataIndex: "organization",
      key: "organization",
    },
    {
      title: "Должность",
      dataIndex: "position",
      key: "position",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Телефон",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "",
      key: "actions",
      render: (_: string, record: { key: string }) => {
        if (transformedData.length < 2) {
          return null;
        }

        return (
          <Can I="access" a="project">
            <Button
              type="link"
              danger
              onClick={() => handleRemoveAdmin(record.key)}
              style={{ position: "relative" }}
            >
              <BasketIcon style={{ position: "relative", top: "-6px" }} />
            </Button>
          </Can>
        );
      },
    },
  ];

  return (
    <>
      <Card style={{ marginTop: 16, borderRadius: "16px" }}>
        <Row
          gutter={[16, 16]}
          style={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Col
            xs={24}
            lg={6}
            style={{ display: "flex", justifyContent: "center" }}
          >
            <ProjectAvatar projectId={projectId} />
          </Col>
          <Col xs={24} lg={18} style={{ paddingLeft: "16px" }}>
            <div className="project-info-container">
              <div className="project-title-container">
                <div className="project-title">{project?.shortName}</div>
              </div>
              <div className="project-description">{project?.fullName}</div>
              <div className="description">
                <div className="description-item">
                  <span className="label">Адрес</span>
                  <span className="value">{project?.addressUnstructured}</span>
                </div>
                <div className="description-item">
                  <span className="label">ID проекта</span>
                  <span className="value">
                    <div>{project?.projectId}</div>
                    <Button type="link" onClick={onCopyIdClick}>
                      Скопировать
                    </Button>
                  </span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
      <div className="table-container project-detail-admin-table-container">
        <Space
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 24,
          }}
        >
          <Typography.Title level={4} style={{ marginBottom: 0 }}>
            Администраторы проекта
          </Typography.Title>
          <Can I="access" a="project">
            <Button type="link" onClick={onOpen}>
              Добавить
            </Button>
          </Can>
        </Space>
        <Table
          loading={isLoading || isPendingRemove}
          columns={columns}
          dataSource={transformedData}
          pagination={false}
          bordered={false}
        />
      </div>
      <RepresentationModal
        isOpen={isOpen}
        handleClose={onClose}
        title="Добавить администратора"
        type="settings"
        isLoading={isPending}
        handleAdd={handleAddAdmin}
        error={error?.message}
        key={key}
      />
    </>
  );
}
